import { useMemo, useState } from 'react'
import { faCalculator } from '@fortawesome/pro-regular-svg-icons'
import { Card, CardColors, Icon, Popup, Select, Text } from '@/ui'
import { TypographyStyles } from '@/styles'
import {
  CreateUserIncomePlanSource,
  selectUserIncomePlanById,
  selectUserIncomePlans,
  UserIncomePlan,
} from '@/features/income-planning/incomePlanning.slice'
import { getPlanTotals } from '@/features/income-planning/utils'
import {
  NonTherapyIncomePopup,
  PerSessionRatePopup,
  SessionsPerMonthPopup,
  TherapyIncomePopup,
  TotalPlannedIncomePopup,
} from '@/features/income-planning/IncomePlanningPopups'
import { formatCurrencyFromCents } from '@/utils/currencyHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { DifferenceArrow } from '@/features/income-planning/DifferenceArrow'

export const PlanInfoCard = ({
  className,
  showComparePlan,
  initialPlanToCompare,
  sentPlan,
  sources,
}: {
  className?: string
  showComparePlan: boolean
  initialPlanToCompare?: number | null
  sentPlan?: UserIncomePlan | null
  sources?: CreateUserIncomePlanSource[]
}) => {
  const plans = useReselector(selectUserIncomePlans)
  const planOptions = useMemo(
    () =>
      Object.values(plans).map((p) => ({
        value: p.id,
        text: p.planName,
      })),
    [plans]
  )

  const [comparePlanId, setComparePlanId] = useState(
    initialPlanToCompare || undefined
  )
  const comparePlan = useReselector(selectUserIncomePlanById, comparePlanId)

  const planTotals = useMemo(() => {
    if (sources) {
      return getPlanTotals(sources)
    } else if (sentPlan) {
      return sentPlan
    }

    // one of sources or sentPlan should always be sent
    return {
      totalPlannedIncomeInCents: null,
      therapyIncomeInCents: null,
      nonTherapyIncomeInCents: null,
      ratePerSession: null,
      sessionsPerMonth: null,
    }
  }, [sentPlan, sources])

  return (
    <Card className={className} color={CardColors.LEVEL_1}>
      <div className="flex">
        <Icon icon={faCalculator} />
        <Text variant={TypographyStyles.LABEL_MD}>
          {sentPlan?.planName || 'Planned Income'}
        </Text>
      </div>

      {showComparePlan && (
        <>
          <Text variant={TypographyStyles.LABEL_XS} className="mt-6">
            Compare to:
          </Text>
          <Select
            options={planOptions}
            placeholder="Select plan"
            variant="text"
            value={comparePlanId}
            onValueChange={setComparePlanId}
            disabled={!planOptions.length}
          />
        </>
      )}

      <div className="mt-6 flex items-center">
        <Text variant={TypographyStyles.DISPLAY_SM} className="mr-2">
          {planTotals.totalPlannedIncomeInCents
            ? formatCurrencyFromCents(planTotals.totalPlannedIncomeInCents)
            : '$-'}
        </Text>
        {planTotals.totalPlannedIncomeInCents &&
        comparePlan?.totalPlannedIncomeInCents &&
        planTotals.totalPlannedIncomeInCents !==
          comparePlan?.totalPlannedIncomeInCents ? (
          <DifferenceArrow
            isUpward={
              (comparePlan?.totalPlannedIncomeInCents || 0) <
              planTotals.totalPlannedIncomeInCents
            }
            textStyle={TypographyStyles.LABEL_XS}
            text={formatCurrencyFromCents(
              Math.abs(
                comparePlan?.totalPlannedIncomeInCents -
                  planTotals.totalPlannedIncomeInCents
              )
            )}
          />
        ) : null}
      </div>
      <Popup
        content={<TotalPlannedIncomePopup />}
        trigger={
          <Text variant={TypographyStyles.POPUP_LINK}>
            Total Planned Income
          </Text>
        }
      />

      <div className="mt-6 flex items-center">
        <Text variant={TypographyStyles.DISPLAY_SM} className="mr-2">
          {planTotals.therapyIncomeInCents
            ? formatCurrencyFromCents(planTotals.therapyIncomeInCents)
            : '$-'}
        </Text>
        {planTotals.therapyIncomeInCents &&
        comparePlan?.therapyIncomeInCents &&
        planTotals.therapyIncomeInCents !==
          comparePlan?.therapyIncomeInCents ? (
          <DifferenceArrow
            isUpward={
              (comparePlan?.therapyIncomeInCents || 0) <
              planTotals.therapyIncomeInCents
            }
            textStyle={TypographyStyles.LABEL_XS}
            text={formatCurrencyFromCents(
              Math.abs(
                comparePlan?.therapyIncomeInCents -
                  planTotals.therapyIncomeInCents
              )
            )}
          />
        ) : null}
      </div>
      <Popup
        content={<TherapyIncomePopup />}
        trigger={
          <Text variant={TypographyStyles.POPUP_LINK}>Therapy Income</Text>
        }
      />
      {Boolean(planTotals.nonTherapyIncomeInCents) && (
        <>
          <div className="mt-6 flex items-center">
            <Text variant={TypographyStyles.DISPLAY_SM} className="mr-2">
              {planTotals.nonTherapyIncomeInCents
                ? formatCurrencyFromCents(planTotals.nonTherapyIncomeInCents)
                : '$-'}
            </Text>
            {planTotals.nonTherapyIncomeInCents &&
            comparePlan?.nonTherapyIncomeInCents &&
            planTotals.nonTherapyIncomeInCents !==
              comparePlan?.nonTherapyIncomeInCents ? (
              <DifferenceArrow
                isUpward={
                  (comparePlan?.nonTherapyIncomeInCents || 0) <
                  planTotals.nonTherapyIncomeInCents
                }
                textStyle={TypographyStyles.LABEL_XS}
                text={formatCurrencyFromCents(
                  Math.abs(
                    comparePlan?.nonTherapyIncomeInCents -
                      planTotals.nonTherapyIncomeInCents
                  )
                )}
              />
            ) : null}
          </div>

          <Popup
            content={<NonTherapyIncomePopup />}
            trigger={
              <Text variant={TypographyStyles.POPUP_LINK}>
                Non-Therapy Income
              </Text>
            }
          />
        </>
      )}

      <div className="mt-6 flex items-center">
        <Text variant={TypographyStyles.DISPLAY_SM} className="mr-2">
          {planTotals.ratePerSession
            ? formatCurrencyFromCents(planTotals.ratePerSession)
            : '$-'}
        </Text>
        {planTotals.ratePerSession &&
        comparePlan?.ratePerSession &&
        planTotals.ratePerSession !== comparePlan?.ratePerSession ? (
          <DifferenceArrow
            isUpward={comparePlan?.ratePerSession < planTotals.ratePerSession}
            textStyle={TypographyStyles.LABEL_XS}
            text={formatCurrencyFromCents(
              Math.abs(comparePlan?.ratePerSession - planTotals.ratePerSession)
            )}
          />
        ) : null}
      </div>
      <Popup
        content={<PerSessionRatePopup />}
        trigger={
          <Text variant={TypographyStyles.POPUP_LINK}>
            Blended per Session Rate
          </Text>
        }
      />
      <div className="mt-6 flex items-center">
        <Text variant={TypographyStyles.DISPLAY_SM} className="mr-2">
          {planTotals.sessionsPerMonth || '-'}
        </Text>

        {planTotals.sessionsPerMonth &&
        comparePlan?.sessionsPerMonth &&
        planTotals.sessionsPerMonth !== comparePlan?.sessionsPerMonth ? (
          <DifferenceArrow
            isUpward={
              (comparePlan?.sessionsPerMonth || 0) < planTotals.sessionsPerMonth
            }
            textStyle={TypographyStyles.LABEL_XS}
            text={Math.abs(
              comparePlan?.sessionsPerMonth - planTotals.sessionsPerMonth
            ).toString()}
          />
        ) : null}
      </div>
      <Popup
        content={<SessionsPerMonthPopup />}
        trigger={
          <Text variant={TypographyStyles.POPUP_LINK}>Sessions per Month</Text>
        }
      />
    </Card>
  )
}
